import { readPreviewTeam } from '../../../../service/previewStorage';
import React, { useMemo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Adsense } from '@ctrl/react-adsense';
import { RampUnit } from "@playwire/pw-react-component";
import Table from './components/Table';

import { SquadPreloader } from '../../../../components/ContentPreloaders';
import FallbackContentAnimation from '../../../../components/FallbackContentAnimation';

import { StyledTableContainer, StyledPlaceholder } from './styled';
import {
  teamAddToList,
  teamGetMyTeam,
  teamGetMyStrategy,
} from '../../../../redux/actions/Team';
import { sidebarOpenRightPanel } from '../../../../redux/actions/Sidebar';
import { transferShowUnshowFilter, transferGetDataToSearchable } from '../../../../redux/actions/Transfers';
// import { adsClickRequest } from '../../../../redux/actions/ads';
import { allowTour } from '../../../../redux/actions/TourControl';
import { isRightPanelOpen } from '../../../../redux/selector/Sidebar';
import { getMyAllSquad, myTeamLoading } from '../../../../redux/selector/Team';
import {
  getMyCurrentTeam,
  isPremiumUser,
  isAuth,
} from '../../../../redux/selector/User';
import { getTourState } from '../../../../redux/selector/TourControl';
import { getSearchPlayer } from '../../../../redux/selector/Transfers';

import {
  formRangeStatus,
  sortingPriority,
  simplifiedPosition,
  insightFilterOptions,
  pickLifeStage,
  playerRecordId,
} from '../../../../service/data';
import { ratingRangeStatus } from '../../../../service/constants';
import exactValueFilter from '../../../../components/TableFilters/exactValueFilter';
import { shouldAutoStartTour, tourStorageValue } from '../../../../helpers/tour/locationToStorage';
import { PLAYER_INFO_STORAGE } from '../../../../helpers/tour/constants';

export default function TableContainer() {
  const location = useLocation();
  const dispatch = useDispatch();
  const team = useSelector(getMyAllSquad);
  const isSidebarOpen = useSelector(isRightPanelOpen);
  const isTourStart = useSelector(getTourState);
  const isTeamLoading = useSelector(myTeamLoading);
  const previewTeamData = readPreviewTeam();
  const isAuthenticated = useSelector(isAuth);

  const myTeamData = useSelector(getMyCurrentTeam);
  const leaguePlayers = useSelector(getSearchPlayer);

  const myTeamName = isAuthenticated ? myTeamData?.name : previewTeamData?.name;
  const isPremium = useSelector(isPremiumUser);
  const getUserToSell = function (user) {
    dispatch(transferShowUnshowFilter(false));
    dispatch(sidebarOpenRightPanel(true));
    dispatch(teamAddToList(user));
  };

  useEffect(() => {
    if (!isTeamLoading) {
      dispatch(teamGetMyTeam());
    }
  }, []);

  useEffect(() => {
    dispatch(teamGetMyStrategy());
  }, [dispatch]);

  useEffect(() => {
    if (!leaguePlayers.length) {
      dispatch(transferGetDataToSearchable());
    }
  }, [dispatch, leaguePlayers.length]);

  useEffect(() => {
    const activeLocalStorageTourState = isSidebarOpen
      ? localStorage.getItem(PLAYER_INFO_STORAGE)
      : localStorage.getItem(tourStorageValue[location.pathname]);
    if (!isTeamLoading && !isTourStart && !activeLocalStorageTourState && shouldAutoStartTour()) {
      dispatch(allowTour(true));
    }
  }, [
    isTeamLoading,
    isTourStart,
    team,
    dispatch,
    location.pathname,
    isSidebarOpen,
  ]);

  /*   useEffect(() => {
    const handleAdsClick = () => {
      if (document.activeElement === document.querySelector('iframe')) {
        dispatch(adsClickRequest());
      }
    };

    window.addEventListener('blur', handleAdsClick);
    return () => {
      window.removeEventListener('blur', handleAdsClick);
    };
  }, []);
 */
  const insightsById = useMemo(() => {
    const map = new Map();
    (leaguePlayers || []).forEach((player) => {
      const id = playerRecordId(player._id || player.id || player);
      if (id) map.set(id, player);
    });
    return map;
  }, [leaguePlayers]);

  const data = useMemo(
    () =>
      team.map(
        ({
          first_name,
          nick_name: nickName,
          last_name,
          fav_position: favPosition,
          second_fav_position: secondFavPosition,
          potential_range: potentialRange,
          form_range: formRange,
          rating_range: ratingRange,
          media_rating,
          ...args
        }) => {
          const fromLeague =
            insightsById.get(playerRecordId(args._id || args.id)) || {};
          return {
          ...args,
          potentialId: potentialRange,
          potentialRange,
          ratingRange,
          first_name,
          last_name,
          potentialValue: ratingRangeStatus[potentialRange] || '-',
          name: `${first_name} ${nickName ? `"${nickName}"` : ''
            }  ${last_name}`,
          formId: formRange,
          form: formRangeStatus[formRange],
          ratingId: ratingRange,
          ratingValue: ratingRangeStatus[ratingRange],
          fav_position: favPosition,
          second_fav_position: secondFavPosition,
          favPosition,
          secondFavPosition,
          media_rating,
          nickName,
          loanedOut: args.loanedOut,
          lenderTeam: args.lender_team,
          sortPriority: sortingPriority[simplifiedPosition[favPosition]],
          team: myTeamName,
          teamName: myTeamName,
          forSale: args.listed ? 'For sale' : 'Not for sale',
          forLoan: args.listed_loan ? 'For loan' : 'Not for loan',
          life_stage: pickLifeStage(args, fromLeague),
          fav_formation: args.fav_formation ?? fromLeague.fav_formation,
          fav_style:
            args.fav_style !== undefined
              ? args.fav_style
              : fromLeague.fav_style,
          fav_mindset: args.fav_mindset ?? fromLeague.fav_mindset,
          worst_mindset: args.worst_mindset ?? fromLeague.worst_mindset,
          fav_opponent: args.fav_opponent ?? fromLeague.fav_opponent,
          worst_opponent: args.worst_opponent ?? fromLeague.worst_opponent,
        };
        }
      ),
    [team, myTeamName, insightsById]
  );

  const columns = useMemo(
    () => [
      {
        Header: 'No',
        accessor: 'number',
        disableSortBy: true,
        disableFilters: true,
      },
      {
        Header: 'Name',
        accessor: 'name',
        width: 120,
      },
      {
        Header: 'Status',
        id: 'statusIcons',
        accessor: '_id',
        disableSortBy: true,
        disableFilters: true,
        width: 126,
      },
      {
        Header: 'Position',
        accessor: 'sortPriority',
        filter: 'equals',
      },
      { Header: 'Age', accessor: 'age', filter: 'ageRange' },
      { Header: 'Rating', accessor: 'ratingId', filter: 'equals' },
      { Header: 'Potential', accessor: 'potentialId', filter: 'equals' },
      { Header: 'Fitness', accessor: 'formId', filter: 'equals' },
      { Header: 'Media Rating', accessor: 'media_rating', disableSortBy: true, disableFilters: true, width: 150 },
      { Header: 'Contract left', accessor: 'contract_remaining_length', disableFilters: true, width: 200 },
      { Header: 'For sale', accessor: 'forSale', filter: 'equals' },
      { Header: 'For loan', accessor: 'forLoan', filter: 'equals' },
      {
        Header: 'Favorite style',
        accessor: 'fav_style',
        filter: 'equals',
        filterGroup: 'advanced',
        filterOptions: insightFilterOptions.fav_style,
        disableSortBy: true,
      },
      {
        Header: 'Favorite mindset',
        accessor: 'fav_mindset',
        filter: 'equals',
        filterGroup: 'advanced',
        filterOptions: insightFilterOptions.fav_mindset,
        disableSortBy: true,
      },
      {
        Header: 'Worst mindset',
        accessor: 'worst_mindset',
        filter: 'equals',
        filterGroup: 'advanced',
        filterOptions: insightFilterOptions.worst_mindset,
        disableSortBy: true,
      },
      {
        Header: 'Favorite opponent',
        accessor: 'fav_opponent',
        filter: 'equals',
        filterGroup: 'advanced',
        disableSortBy: true,
      },
      {
        Header: 'Worst opponent',
        accessor: 'worst_opponent',
        filter: 'equals',
        filterGroup: 'advanced',
        disableSortBy: true,
      },
      {
        Header: 'Development phase',
        accessor: 'life_stage',
        filter: exactValueFilter,
        filterGroup: 'advanced',
        filterOptions: insightFilterOptions.life_stage,
        disableSortBy: true,
      },
    ],
    [data]
  );

  return (
    <>
      <FallbackContentAnimation
        loading={isTeamLoading && team.length === 0}
        repeat="22"
        fallback={
          <StyledPlaceholder>
            <SquadPreloader
              style={{
                width: '100%',
                height: '38px',
                top: '0',
                left: '0',
                bottom: '0',
                right: '0',
              }}
            />
          </StyledPlaceholder>
        }
      >
        <StyledTableContainer>
          <Table
            data={data}
            columns={columns}
            getUserToSell={(usr) => getUserToSell(usr)}
          />
        </StyledTableContainer>
      </FallbackContentAnimation>
      {/*       {!isPremium && (<RampUnit
          type="dfp-med-rect"
          cssClass="leaderboard"
        />)} */}
      {/*       {!isPremium && (
        <Adsense
          client={`${!isPremium ? 'ca-pub-2739224896366063' : ''}`}
          slot={`${!isPremium ? '9437045662' : ''}`}
          style={{
            width: '100%',
            height: `${!isPremium ? 300 : 0}`,
            display: `${!isPremium ? 'flex' : 'none'}`,
            flexDirection: 'center',
            marginTop: '20px',
          }}
          layout="in-article"
          format="fluid"
        />
      )} */}
    </>
  );
}
